import React, {useEffect} from 'react';

import {AnalysisTemplateSelectorQuery} from 'analysis-tool/graphql/__generated/AnalysisTemplateSelectorQuery';
import {
  ANALYSIS_TEMPLATE_SELECTOR_QUERY,
  AnalysisTemplateSelectorTemplate,
} from 'analysis-tool/graphql/AnalysisTemplateSelectorQuery';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import SelectDropdown, {SelectDropdownProps} from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {AnalysisTemplateGraphQL} from './AnalysisTemplateTypes';
import {GetAnalysisTemplate} from './graphql/__generated/GetAnalysisTemplate';
import {GET_ANALYSIS_TEMPLATE_QUERY} from './graphql/GetAnalysisTemplate';
import {TemplateApplyStateType} from './useAnalysisTemplateSelectorState';

interface AnalysisTemplateSelectorProps {
  templateApplyState: TemplateApplyStateType;
  // SelectId has to be a part of global state or else when the global state is deleted, this component will
  // just repopulate the template.
  onSelectId: (selectedTemplateId: string | null) => void;
  onSelect: (selectedTemplateId: AnalysisTemplateGraphQL | null) => void;
  selectDropdownProps?: Partial<Omit<SelectDropdownProps, 'menu' | 'displayName' | 'dataTestAttrs'>>;
  filterTemplate?: (template: AnalysisTemplateSelectorTemplate) => boolean;
  includeNone?: boolean;
}

/** Renders a searchable dropdown menu for selecting from existing analysis templates.
 *
 * @param value - The currently selected analysis template.
 * @param onSelect - The callback to call when a template is selected from the dropdown options.
 * @param selectDropdownProps - Additional props to pass to the SelectDropdown component.
 * @param filterTemplate - An optional filter function to apply to the templates. If the function returns false, the template will not be displayed in the dropdown.
 * @param includeNone - An optional flag to include a "None" option in the dropdown.
 * @returns The rendered searchable dropdown menu.
 */
export default function AnalysisTemplateSelector({
  templateApplyState,
  onSelectId,
  onSelect,
  selectDropdownProps,
  filterTemplate,
  includeNone,
}: AnalysisTemplateSelectorProps): JSX.Element | null {
  const templateQueryResult = useSkippableBenchlingQuery<GetAnalysisTemplate>(GET_ANALYSIS_TEMPLATE_QUERY, {
    skip:
      templateApplyState.selectedTemplateId == null ||
      templateApplyState.selectedTemplateId === templateApplyState.selectedTemplate?.id,
    variables: {analysisTemplateId: templateApplyState.selectedTemplateId},
  });

  useEffect(() => {
    if (
      templateQueryResult.type === QueryResultType.SUCCESS &&
      !isInaccessible(templateQueryResult.data.analysisTemplate) &&
      templateApplyState.selectedTemplateId !== templateApplyState.selectedTemplate?.id
    ) {
      onSelect(templateQueryResult.data.analysisTemplate);
    }
  }, [templateApplyState, onSelect, templateQueryResult]);

  const queryResult = useBenchlingQuery<AnalysisTemplateSelectorQuery>(ANALYSIS_TEMPLATE_SELECTOR_QUERY);
  if (queryResult.type === QueryResultType.ERROR) {
    return null;
  } else if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  const queryResultOptionDatas = queryResult.data.analysisTemplateListing
    .filter((template) => filterTemplate?.(template) ?? true)
    .map((template) => ({
      name: template.name,
      value: template,
      dataTestAttrs: {
        component: 'AnalysisTemplateSelector',
        element: 'option',
        key: template.id,
      },
    }));

  const queryResultOptionDatasWithNone = [
    {
      name: 'None',
      value: null,
    },
    ...queryResultOptionDatas,
  ];

  if (templateQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  return (
    <SelectDropdown
      menu={
        <SearchableMenu
          onSelect={(analysisTemplate: AnalysisTemplateSelectorTemplate | null) => {
            if (analysisTemplate == null) {
              onSelectId(null);
              onSelect(null);
              return;
            }
            onSelectId(analysisTemplate.id);
          }}
          optionDatas={includeNone ? queryResultOptionDatasWithNone : queryResultOptionDatas}
          selectedValue={templateApplyState.selectedTemplateId}
          width={Menu.WIDTHS.FULL}
        />
      }
      displayName={
        templateApplyState.selectedTemplate == null
          ? 'Select template...'
          : templateApplyState.selectedTemplate.name
      }
      dataTestAttrs={{
        component: 'AnalysisTemplateSelector',
        element: 'root',
      }}
      {...selectDropdownProps}
    />
  );
}
